/** @jsxImportSource @emotion/react */
import { PageWrapper } from "../../components/page-skeleton/PageWrapper";
import { CardContainer } from "../../components/page-skeleton/CardContainer";
import { PageHeader } from "../../components/page-skeleton/PageHeader";
import { Table } from "../../components/table-components/Table";
import { TableRowHeader } from "../../components/table-components/TableRowHeader";
import { TableCellHeaderText } from "../../components/table-components/TableCellHeaderText";
import { TableBody } from "../../components/table-components/TableBody";
import { ReportsTableRow } from "./components/ReportsTableRow";
import { PageFooter } from "../../components/page-skeleton/PageFooter";
import { StyledText } from "../../assets/typography/typography";
import moment, { Moment } from "moment";
import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { ButtonExternalLink } from "../../components/NewButtons";
import { ReportsLinkTableRow } from "./components/ReportsLinkTableRow";

const salesDocumentationLink =
  "https://heapsapp.notion.site/Sales-report-Documentation-be4d69ec15cf4c7995cde384cc9f1065";

type ExportOption = {
  startDate: Moment;
  endDate: Moment;
  label: string;
  icon: React.ReactNode;
};

export type ReportType = {
  title: string;
  description?: string;
  category: "Sales";
  reportApiUrlKey: string;
  exportOptions: ExportOption[];
  explainationBoxTitle?: string;
  explainationBoxSubtitle?: string;
  monthlyExportOnly: boolean;
};

export type PreviewReportType = Omit<ReportType, "exportOptions">;

const currentMonthOption: ExportOption = {
  startDate: moment().startOf("month").hour(0).minute(0).second(0),
  endDate: moment().hour(23).minute(59).second(59),
  label: "Export for this month to date",
  icon: <FontAwesomeIcon icon={solid("calendar-clock")} />,
};

const lastMonthOption: ExportOption = {
  startDate: moment()
    .subtract(1, "month")
    .startOf("month")
    .hour(0)
    .minute(0)
    .second(0),
  endDate: moment()
    .subtract(1, "month")
    .endOf("month")
    .hour(23)
    .minute(59)
    .second(59),
  label: "Export for last month",
  icon: <FontAwesomeIcon icon={solid("calendars")} />,
};

const currentMonthToDateAndLastMonthOptions = [
  currentMonthOption,
  lastMonthOption,
];

export const reportTypes: ReportType[] = [
  // Hiding for now until further notice by operations
  {
    title: "Total sales via app, webshop, kiosk and QR",
    description: "Incl. VAT.",
    category: "Sales",
    reportApiUrlKey: "revenue-and-balances",
    exportOptions: currentMonthToDateAndLastMonthOptions,
    monthlyExportOnly: false,
  },
  {
    title: "Sales per payment source",
    description: "Breakdown by shop. Incl. VAT.",
    category: "Sales",
    reportApiUrlKey: "cashflow-by-gateway",
    exportOptions: currentMonthToDateAndLastMonthOptions,
    monthlyExportOnly: false,
  },
  {
    title: "Balances: Voucher, Giftcard, Top-up",
    description: "Incl. VAT",
    category: "Sales",
    reportApiUrlKey: "balances",
    exportOptions: [lastMonthOption],
    monthlyExportOnly: true,
  },
  {
    title: "Transactions",
    description: "Incl. VAT.",
    category: "Sales",
    reportApiUrlKey: "transaction-history",
    exportOptions: currentMonthToDateAndLastMonthOptions,
    monthlyExportOnly: false,
  },
  {
    title: "Refunds",
    description: "Incl. VAT.",
    category: "Sales",
    reportApiUrlKey: "refunds",
    exportOptions: currentMonthToDateAndLastMonthOptions,
    monthlyExportOnly: false,
  },
  {
    title: "Vouchers spent",
    description: "Incl. VAT.",
    category: "Sales",
    reportApiUrlKey: "vouchers-spent",
    exportOptions: currentMonthToDateAndLastMonthOptions,
    monthlyExportOnly: false,
  },
  {
    title: "Vouchers created",
    description: "All vouchers created",
    category: "Sales",
    reportApiUrlKey: "vouchers-created",
    exportOptions: currentMonthToDateAndLastMonthOptions,
    monthlyExportOnly: false,
  },
  {
    title: "Vouchers removed",
    description: "All vouchers removed",
    category: "Sales",
    reportApiUrlKey: "vouchers-removed",
    exportOptions: currentMonthToDateAndLastMonthOptions,
    monthlyExportOnly: false,
  },
  // Add here for more reports
];

export const previewReports: PreviewReportType[] = [
  {
    title: "Credit card revenue via app, webshop, kiosk and QR",
    description: "Incl. VAT.",
    category: "Sales",
    reportApiUrlKey: "totals-by-shop",
    monthlyExportOnly: false,
  },
];

export const Reports = () => {
  return (
    <PageWrapper>
      <CardContainer>
        <PageHeader
          title="Reports"
          subtitle="All reports available are listed below"
        >
          <ButtonExternalLink
            href={salesDocumentationLink}
            icon={
              <FontAwesomeIcon icon={solid("arrow-up-right-from-square")} />
            }
          >
            Learn more
          </ButtonExternalLink>
        </PageHeader>
        <div>
          <Table>
            <thead>
              <TableRowHeader>
                <TableCellHeaderText>Report</TableCellHeaderText>
                <TableCellHeaderText>Report description</TableCellHeaderText>
                <TableCellHeaderText>Category</TableCellHeaderText>
                <th />
              </TableRowHeader>
            </thead>
            <TableBody>
              {reportTypes.map((report, i) => {
                return <ReportsTableRow key={i} report={report} />;
              })}
              {previewReports.map((report, i) => {
                return <ReportsLinkTableRow key={i} report={report} />;
              })}
            </TableBody>
          </Table>
          <PageFooter>
            <StyledText className="bodyTextRegular mb-0">
              {reportTypes?.length + previewReports?.length}{" "}
              {reportTypes?.length + previewReports?.length === 1
                ? "report"
                : "reports"}{" "}
              in total
            </StyledText>
          </PageFooter>
        </div>
      </CardContainer>
    </PageWrapper>
  );
};
